import type { QueryClient } from '@tanstack/react-query';
import { listContainers } from '@/services/api/endpoints';
import { projectNumberCategory } from '@/services/office/categories';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import type { Container, Folder } from '@/types/domain';

/**
 * Numéro d'affaire à poser en catégorie pour une destination de rangement, ou
 * `null` s'il n'y en a pas.
 *
 * `null` couvre TROIS cas parfaitement normaux, qu'aucun n'a à être signalé :
 * l'espace d'équipe (aucun numéro), le projet dont `number` porte un intitulé
 * interne et non un numéro (cf. `projectNumberCategory`, et c'est 16 projets sur
 * 140 en production), et le conteneur introuvable.
 *
 * POURQUOI `ensureQueryData` ET NON `getQueryData`. La liste des conteneurs est
 * déjà en cache dans tous les parcours réels — on vient de choisir une
 * destination dans un arbre qui l'affiche. Mais s'en remettre au cache ferait
 * dépendre une ÉTIQUETTE d'un état d'affichage : le jour où un parcours range
 * sans avoir monté l'arbre, la catégorie disparaîtrait sans que rien ne le dise.
 * `ensureQueryData` rend le cache quand il existe et le remplit sinon, avec la
 * même clé que `useContainers` — donc gratuit dans le cas courant, correct dans
 * l'autre.
 */
async function containerNumber(qc: QueryClient, containerId: string): Promise<string | null> {
  try {
    const containers = await qc.ensureQueryData<Container[]>({
      queryKey: ['containers'],
      queryFn: listContainers,
    });
    const found = containers.find((c) => c.id === containerId);
    if (!found) {
      addBreadcrumb({
        category: 'mutation',
        message: 'destination container absent de la liste',
        data: { containerId },
      });
      return null;
    }
    return projectNumberCategory(found.number);
  } catch {
    // Liste illisible (réseau, jeton) : on range sans étiquette de numéro plutôt
    // que de faire échouer le rangement pour une catégorie.
    return null;
  }
}

/**
 * Résout le numéro d'affaire de la destination, que l'appelant connaisse le
 * projet ou seulement le dossier.
 *
 * DEUX ENTRÉES, et l'ordre compte. `containerId` est la voie SÛRE : les chemins
 * « Ranger ici » le transmettent depuis le point de clic, où il est toujours
 * connu, et rien n'est alors deviné. Le glisser-déposer, lui, n'a parfois que
 * l'identifiant du dossier : la cible est lue dans le DOM
 * (`data-drop-folder-id`), et le marqueur du projet n'est pas un ancêtre du
 * dossier sur la page détail. On retombe donc sur le cache des dossiers — ce qui
 * est légitime ICI et seulement ici : un dossier ne peut être une cible de dépôt
 * que s'il est RENDU, donc que si `useFolders` a rempli ce cache.
 */
export async function destinationProjectNumber(
  qc: QueryClient,
  dest: { containerId?: string | null; folderId?: string | null },
): Promise<string | null> {
  if (dest.containerId) return containerNumber(qc, dest.containerId);
  if (!dest.folderId) return null;

  // `['folders', <containerId>]` : on ne sait pas lequel, d'où le balayage des
  // listes déjà chargées. Elles se comptent en unités (un espace ouvert = une
  // entrée), pas en milliers.
  for (const [, folders] of qc.getQueriesData<Folder[]>({ queryKey: ['folders'] })) {
    const hit = folders?.find((f) => f.id === dest.folderId);
    if (hit) return containerNumber(qc, hit.containerId);
  }
  addBreadcrumb({
    category: 'mutation',
    message: 'projet de la destination non résolu',
    data: { folderId: dest.folderId },
  });
  return null;
}
